/**
 * AccessMap Business Rules
 *
 * Centralized business rules and logic for access map operations.
 * This module provides a single source of truth for access map business logic.
 */

import {
  getRoleAccess,
  setRoleAccess,
  getHighestPublicRole,
  parseAccessMapFromBackend,
} from './accessMapUtils';
import {
  ROLE_TYPES,
  ACCESS_TYPES,
  TAB_NAMES,
  PUBLIC_ACCESS_OPTIONS,
  PUBLIC_ACCESS_LABELS,
  uiOptionToPublicAccessPolicy,
  publicAccessPolicyToUiOption,
} from './accessMapConstants';

// ============================================================================
// TAB FILTERING RULES
// ============================================================================

/**
 * Gets the filter rule for a specific tab
 * Returns a function that can be used to filter items
 *
 * Note: This function is mainly for documentation/reference.
 * The actual filtering is done by filterByTab in accessMapFilters.js
 *
 * @param {string} tabName - The tab name (myProjects, discovery, etc.)
 * @returns {string} - Description of the filter rule
 */
export const getTabFilterRule = (tabName) => {
  switch (tabName) {
    case TAB_NAMES.MY_PROJECTS:
    case TAB_NAMES.MY_COLLECTIONS:
    case TAB_NAMES.MY_ITEMS:
      return 'Show items where user has ANY explicit role (OWNER, CONTRIBUTOR, or VIEWER)';

    case TAB_NAMES.DISCOVERY:
      return 'Show items that are public (any role is PUBLIC) AND user has NO explicit role';

    default:
      return 'Unknown tab';
  }
};

// ============================================================================
// PUBLIC ACCESS RADIO BUTTON RULES
// ============================================================================

/**
 * Applies public access option selection (radio button) to accessMap
 *
 * Mapping (UI → BE publicAccessPolicy RoleType):
 * - Default: [DEFAULT] — inherit parent
 * - Protected: [NONE] — explicitly private
 * - View: [VIEWER]
 * - Edit: [CONTRIBUTOR]
 *
 * OWNER is always RESTRICTED for public access (only assigned owners can control).
 *
 * @param {Object} accessMap - Current accessMap
 * @param {string} option - PUBLIC_ACCESS_OPTIONS.DEFAULT | PROTECTED | VIEW | EDIT
 * @returns {Object} - Updated accessMap
 */
export const applyPublicAccessOption = (accessMap, option) => {
  if (!accessMap || !option) return accessMap;

  let updated = { ...accessMap };

  if (!updated.roleAccessList) {
    updated.roleAccessList = {};
  }

  updated = setRoleAccess(updated, ROLE_TYPES.OWNER, ACCESS_TYPES.RESTRICTED);

  const resolvedOption =
    option === PUBLIC_ACCESS_OPTIONS.NONE
      ? PUBLIC_ACCESS_OPTIONS.PROTECTED
      : option;

  switch (resolvedOption) {
    case PUBLIC_ACCESS_OPTIONS.DEFAULT:
      updated = setRoleAccess(updated, ROLE_TYPES.CONTRIBUTOR, ACCESS_TYPES.RESTRICTED);
      updated = setRoleAccess(updated, ROLE_TYPES.VIEWER, ACCESS_TYPES.RESTRICTED);
      updated.publicAccessPolicy = uiOptionToPublicAccessPolicy(PUBLIC_ACCESS_OPTIONS.DEFAULT);
      break;

    case PUBLIC_ACCESS_OPTIONS.PROTECTED:
      updated = setRoleAccess(updated, ROLE_TYPES.CONTRIBUTOR, ACCESS_TYPES.RESTRICTED);
      updated = setRoleAccess(updated, ROLE_TYPES.VIEWER, ACCESS_TYPES.RESTRICTED);
      updated.publicAccessPolicy = uiOptionToPublicAccessPolicy(PUBLIC_ACCESS_OPTIONS.PROTECTED);
      break;

    case PUBLIC_ACCESS_OPTIONS.VIEW:
      updated = setRoleAccess(updated, ROLE_TYPES.CONTRIBUTOR, ACCESS_TYPES.RESTRICTED);
      updated = setRoleAccess(updated, ROLE_TYPES.VIEWER, ACCESS_TYPES.PUBLIC);
      updated.publicAccessPolicy = uiOptionToPublicAccessPolicy(PUBLIC_ACCESS_OPTIONS.VIEW);
      break;

    case PUBLIC_ACCESS_OPTIONS.EDIT:
      updated = setRoleAccess(updated, ROLE_TYPES.CONTRIBUTOR, ACCESS_TYPES.PUBLIC);
      updated = setRoleAccess(updated, ROLE_TYPES.VIEWER, ACCESS_TYPES.PUBLIC);
      updated.publicAccessPolicy = uiOptionToPublicAccessPolicy(PUBLIC_ACCESS_OPTIONS.EDIT);
      break;

    default:
      updated = setRoleAccess(updated, ROLE_TYPES.CONTRIBUTOR, ACCESS_TYPES.RESTRICTED);
      updated = setRoleAccess(updated, ROLE_TYPES.VIEWER, ACCESS_TYPES.RESTRICTED);
      updated.publicAccessPolicy = uiOptionToPublicAccessPolicy(PUBLIC_ACCESS_OPTIONS.PROTECTED);
  }

  // Drop removed BE field if present from older FE state.
  delete updated.publicAccessMode;

  return updated;
};

/**
 * Gets the current public access option from accessMap.
 * Uses publicAccessPolicy; empty policy → Default.
 *
 * @param {Object} accessMap - The accessMap to analyze
 * @returns {string} - PUBLIC_ACCESS_OPTIONS value
 */
export const getPublicAccessOptionFromAccessMap = (accessMap) => {
  if (Array.isArray(accessMap?.publicAccessPolicy)) {
    return publicAccessPolicyToUiOption(accessMap.publicAccessPolicy);
  }

  if (!accessMap?.roleAccessList) {
    return PUBLIC_ACCESS_OPTIONS.DEFAULT;
  }

  const contributorPublic = getRoleAccess(accessMap, ROLE_TYPES.CONTRIBUTOR) === ACCESS_TYPES.PUBLIC;
  const viewerPublic = getRoleAccess(accessMap, ROLE_TYPES.VIEWER) === ACCESS_TYPES.PUBLIC;

  if (contributorPublic && viewerPublic) {
    return PUBLIC_ACCESS_OPTIONS.EDIT;
  }
  if (viewerPublic && !contributorPublic) {
    return PUBLIC_ACCESS_OPTIONS.VIEW;
  }
  return PUBLIC_ACCESS_OPTIONS.DEFAULT;
};

// ============================================================================
// PUBLIC ACCESS LEVEL DETERMINATION
// ============================================================================

/**
 * Determines the public access level for the status indicator.
 *
 * @returns {{ level: string, message: string }}
 */
export const readItemPublicAccessOption = (accessMap) => {
  const parsed = parseAccessMapFromBackend(accessMap || {});
  return getPublicAccessOptionFromAccessMap(parsed);
};

/** A project has no parent. Stored Default is private, same as Protected. */
export const coerceProjectPublicAccessOption = (option) => {
  if (
    !option ||
    option === PUBLIC_ACCESS_OPTIONS.DEFAULT ||
    option === PUBLIC_ACCESS_OPTIONS.NONE
  ) {
    return PUBLIC_ACCESS_OPTIONS.PROTECTED;
  }
  return option;
};

/**
 * Project public access carried on the collection from getCollData.
 * BE-PENDING: CollData does not send projectPublicAccessPolicy yet.
 * Expected shape matches accessMap.publicAccessPolicy: NONE | VIEWER | CONTRIBUTOR.
 * A missing list stays unresolved so the window does not invent an inherited level.
 */
export const readProjectPublicAccessOption = (collection) => {
  const policy = collection?.projectPublicAccessPolicy;
  if (!Array.isArray(policy) || policy.length === 0) return null;
  return coerceProjectPublicAccessOption(publicAccessPolicyToUiOption(policy));
};

export const inheritedDefaultAccessMessage = (parentOption) => {
  const effective = coerceProjectPublicAccessOption(parentOption);
  if (effective === PUBLIC_ACCESS_OPTIONS.EDIT) {
    return "Default. Inherits edit access from the project.";
  }
  if (effective === PUBLIC_ACCESS_OPTIONS.VIEW) {
    return "Default. Inherits view access from the project.";
  }
  return "Default. Protected, because the project is protected.";
};

/**
 * Access the public can use right now.
 * Default follows the parent. A parent stored as Default counts as Protected.
 * With no parent, Default stays unresolved.
 */
export const resolveEffectivePublicAccessOption = (storedOption, parentOption = null) => {
  const choice =
    storedOption === PUBLIC_ACCESS_OPTIONS.NONE
      ? PUBLIC_ACCESS_OPTIONS.PROTECTED
      : storedOption;

  if (choice === PUBLIC_ACCESS_OPTIONS.DEFAULT) {
    if (!parentOption) return PUBLIC_ACCESS_OPTIONS.DEFAULT;
    return coerceProjectPublicAccessOption(parentOption);
  }
  return choice || PUBLIC_ACCESS_OPTIONS.PROTECTED;
};

/**
 * Card access row: the word is the choice, the icon is the result.
 * Default with a known parent reads "Default (View)" and uses the result icon.
 */
export const getCardPublicAccessDisplay = (storedOption, parentOption = null) => {
  const effective = resolveEffectivePublicAccessOption(storedOption, parentOption);
  const inherited =
    storedOption === PUBLIC_ACCESS_OPTIONS.DEFAULT &&
    effective !== PUBLIC_ACCESS_OPTIONS.DEFAULT;
  const tone =
    effective === PUBLIC_ACCESS_OPTIONS.VIEW ||
    effective === PUBLIC_ACCESS_OPTIONS.EDIT ||
    effective === PUBLIC_ACCESS_OPTIONS.PROTECTED
      ? effective
      : null;
  const choiceLabel =
    PUBLIC_ACCESS_LABELS[storedOption] ||
    PUBLIC_ACCESS_LABELS[effective] ||
    "";

  return {
    iconKey: effective,
    label: inherited ? `Default (${PUBLIC_ACCESS_LABELS[effective]})` : choiceLabel,
    title: inherited ? inheritedDefaultAccessMessage(parentOption) : "Public Access",
    tone,
  };
};

export const getPublicAccessLevel = (accessMap) => {
  const option = getPublicAccessOptionFromAccessMap(accessMap);

  switch (option) {
    case PUBLIC_ACCESS_OPTIONS.EDIT:
      return {
        level: 'edit',
        message: 'Public edit. Everyone can see and edit this item.'
      };
    case PUBLIC_ACCESS_OPTIONS.VIEW:
      return {
        level: 'view',
        message: 'Public view. Everyone can see it; only assigned users can edit it.'
      };
    case PUBLIC_ACCESS_OPTIONS.PROTECTED:
    case PUBLIC_ACCESS_OPTIONS.NONE:
      return {
        level: 'protected',
        message: 'Protected. Only assigned users can access it, even if a parent is public.'
      };
    case PUBLIC_ACCESS_OPTIONS.DEFAULT:
    default:
      return {
        level: 'default',
        message: 'Default. Inherits public access from the parent when set; otherwise private.'
      };
  }
};

/**
 * Gets the highest public role level
 * This is a convenience wrapper around getHighestPublicRole from accessMapUtils
 *
 * @param {Object} accessMap - The accessMap to analyze
 * @returns {string|null} - Highest public role (OWNER, CONTRIBUTOR, VIEWER) or null
 */
export const getHighestPublicRoleLevel = (accessMap) => {
  return getHighestPublicRole(accessMap);
};
